import {makeAutoObservable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import type {Empty, Nullable} from "data/types/generics";
import {Bindings} from "data/constants/bindings";
import type {ISquad, ISquadsStore} from "data/stores/squads/squds.store";
import {IMatch} from "data/types/entities";
import {MatchStatusEnum, Locale} from "data/enums";
import type {ILocalizationStore} from "data/stores/localization/localization.store";

import {DateTime} from "luxon";

export interface IMatchStatusController extends ViewController {
	i18n: ILocalizationStore;

	get getPeriodName(): string;

	get getDate(): string;

	setMatch(match: IMatch): void;

	getSquadByID: (squadId: number) => Empty<ISquad>;
}

@injectable()
export class MatchStatusController implements IMatchStatusController {
	private _match: Nullable<IMatch> = null;

	constructor(
		@inject(Bindings.SquadsStore) private _squadsStore: ISquadsStore,
		@inject(Bindings.LocalizationStore) public i18n: ILocalizationStore
	) {
		makeAutoObservable(this);
	}

	get getPeriodName() {
		if (!this._match) {
			return "";
		}
		const periods: Record<MatchStatusEnum, string> = {
			[MatchStatusEnum.CANCELED]: this.i18n.t("widget.match.canceled", "CANCELED"),
			[MatchStatusEnum.COMPLETE]: this.i18n.t("widget.match.complete", "FINAL"),
			[MatchStatusEnum.FIRST_QUARTER]: this.i18n.t("widget.match.first_quarter", "1sr"),
			[MatchStatusEnum.SECOND_QUARTER]: this.i18n.t("widget.match.second_quarter", "2nd"),
			[MatchStatusEnum.HALF_TIME]: this.i18n.t("widget.match.half_time", "HT"),
			[MatchStatusEnum.THIRD_QUARTER]: this.i18n.t("widget.match.third_quarter", "3rd"),
			[MatchStatusEnum.FOURTH_QUARTER]: this.i18n.t("widget.match.fourth_quarter", "4th"),
			[MatchStatusEnum.OVERTIME]: this.i18n.t("widget.match.overtime", "OT"),
			[MatchStatusEnum.PRE_MATCH]: this.i18n.t("widget.match.pre_match", "PRE"),
			[MatchStatusEnum.SCHEDULED]: this.getTime,
		};

		return periods[this._match.status];
	}

	get getDate() {
		if (!this._match) {
			return "-";
		}

		const format = this.i18n.locale === Locale.EN ? "ccc LLL d" : "ccc d LLL";

		return DateTime.fromISO(this._match.date, {
			locale: this.timeZoneLocale[this.i18n.locale],
		}).toFormat(format);
	}

	private get getTime() {
		if (!this._match) {
			return "-";
		}

		const format = this.i18n.locale === Locale.EN ? "h:mma ZZZZ" : "H:mm ZZZZ";

		return DateTime.fromISO(this._match.date, {
			locale: this.timeZoneLocale[this.i18n.locale],
		}).toFormat(format);
	}

	private get timeZoneLocale() {
		return {
			fr: "fr-CA",
			en: "en-US",
		};
	}
	dispose(): void {
		return;
	}

	init(): void {
		return;
	}

	setMatch(match: IMatch) {
		this._match = match;
	}

	getSquadByID = (squadId: number): Empty<ISquad> => {
		return this._squadsStore.getSquadById(squadId);
	};
}
